import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, BriefcaseIcon, CheckCircle2Icon, HeartIcon, LandmarkIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Container } from '../components/ui/Container';
import { GeometricAccent } from '../components/ui/GeometricAccent';
import { MosqueArchMask } from '../components/ui/MosqueArchMask';
import { PatternDecor } from '../components/ui/PatternDecor';
type SupportOption = {
  title: string;
  body: string;
  action: string;
  to: string;
  tone: string;
};
type PartnershipModel = {
  icon: typeof HeartIcon;
  title: string;
  body: string;
  tone: string;
};
const supportOptions: SupportOption[] = [{
  title: 'التبرع للأطفال',
  body: 'التعليم هو أكثر أشكال الحماية استدامة للأطفال المسلمين الذين ينشأون ضمن سياقات الأقلية.',
  action: 'تبرّع',
  to: '/ar/partnership/donate-for-children',
  tone: 'bg-sage-100'
}, {
  title: 'برنامج المنح الدراسية',
  body: 'تضمن المنح تكافؤ الوصول والاستمرارية التعليمية للأسر التي تواجه صعوبات، عبر تخصيص وإبلاغ واضحين.',
  action: 'استكشف المنح',
  to: '/ar/partnership/scholarships',
  tone: 'bg-[#F7EDE9]'
}, {
  title: 'الشركاء المؤسسيون',
  body: 'مبادرات قابلة للتوسع تمتد لسنوات، مع حوكمة واضحة وإشراف أخلاقي وتقارير أثر للمؤسسات والجهات الداعمة.',
  action: 'اطلب نبذة للشركاء',
  to: '/ar/partnership/institutional-partners',
  tone: 'bg-sand-100'
}, {
  title: 'كن شريكًا',
  body: 'نتعاون مع المؤسسات والخبراء الذين يضعون المسؤولية والاستدامة طويلة الأجل والأثر القابل للقياس في الأولوية.',
  action: 'تقدّم للشراكة',
  to: '/ar/partnership/become-a-partner',
  tone: 'bg-[#EDF2F7]'
}];
const partnershipModels: PartnershipModel[] = [{
  icon: HeartIcon,
  title: 'المانحون الأفراد',
  body: 'هبات متكررة أو لمرة واحدة تموّل المنح والمواد التعليمية وتنفيذ البرامج.',
  tone: 'bg-sage-100'
}, {
  icon: LandmarkIcon,
  title: 'الشركاء المؤسسيون',
  body: 'منظمات غير حكومية ومؤسسات وجهات حكومية تعمل معًا في مشاريع أثر طويلة الأجل.',
  tone: 'bg-[#EDF2F7]'
}, {
  icon: BriefcaseIcon,
  title: 'الشركات والمسؤولية المجتمعية',
  body: 'شراكات مسؤولية مجتمعية مع مؤسسات تتوافق أنشطتها الأساسية مع المبادئ الأخلاقية الإسلامية.',
  tone: 'bg-sand-100'
}];
const reports = [{
  title: 'تقارير أثر فصلية',
  body: 'التقدم والمخرجات والنتائج، مصممة لمراجعة الشركاء وتحديث أصحاب المصلحة.',
  tone: 'bg-sage-100'
}, {
  title: 'ملخص مالي سنوي',
  body: 'نظرة واضحة على توزيع الأموال وتكاليف البرامج، وجاهزة للتدقيق عند الاقتضاء.',
  tone: 'bg-[#F7EDE9]'
}, {
  title: 'الحوكمة والحماية',
  body: 'سياسات وأدوار وضمانات تدعم التنفيذ المسؤول والمساءلة.',
  tone: 'bg-sand-100'
}];
export function ArabicPartnership() {
  return <div dir="rtl" className="overflow-hidden text-right" data-id="element-2810" data-mp="9zsa7oj1c2qh">
      <section className="relative isolate overflow-hidden" data-id="element-2811" data-mp="4ycr420f46aq">
        <PatternDecor side="right" y="middle" size="lg" data-id="element-2812" data-mp="1ldns351bqple" />
        <GeometricAccent variant="dots" className="absolute left-6 top-8 h-32 w-32" opacity={0.4} data-id="element-2813" data-mp="1lc2vzazd2ylf" />
        <Container className="relative pb-16 pt-8 md:pb-24 md:pt-12" data-id="element-2814" data-mp="1ge1e2tdo95bj">
          <div className="grid items-center gap-10 lg:grid-cols-12" data-id="element-2815" data-mp="1ovbrbkooko3y">
            <motion.div initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            ease: [0.22, 1, 0.36, 1]
          }} className="lg:col-span-6" data-id="element-2816" data-mp="1sx6tssicl7jb">
              <p className="text-sm font-semibold text-forest-700" data-id="element-2817" data-mp="1ez8ixx38jt8k">دعم استراتيجي للأسر المسلمة والتعليم والاستقرار طويل الأجل</p>
              <h1 className="mt-3 font-display text-4xl font-semibold leading-[1.45] text-ink-900 md:text-6xl" data-id="element-2818" data-mp="27m31upkemkjx">شراكات من أجل أثر مستدام</h1>
              <div className="mt-6 max-w-xl space-y-4 text-base leading-loose text-ink-700 md:text-lg" data-id="element-2819" data-mp="2cjup36a3tihs"><p data-id="element-2820" data-mp="1xx1gejv3bayk">المركز الدولي للتعلّم الإسلامي مبادرة تعليمية وداعمة للأسرة، تقودها رسالة تركز على حماية الأطفال والأسر المسلمة التي تعيش كأقليات، وتعزيز الاستقرار والكرامة والاندماج المسؤول.</p><p data-id="element-2821" data-mp="2qkf925rr9kcv">نعمل مع المانحين الاستراتيجيين والمؤسسات والشركاء الذين يشاركوننا رؤية طويلة الأجل للتعليم وحفظ الهوية والتماسك الاجتماعي.</p></div>
              <div className="mt-8 flex flex-wrap gap-3" data-id="element-2822" data-mp="2vcym91mj6dh5"><Link to="/ar/partnership/donate-for-children" data-id="element-2823" data-mp="16pkvrv9ss8g4"><Button variant="primary" data-id="element-2824" data-mp="16re2fakeuso4">تبرّع الآن</Button></Link><Link to="/ar/partnership/become-a-partner" data-id="element-2825" data-mp="12ohf56jxe8du"><Button variant="secondary" data-id="element-2826" data-mp="12ng4v4aqhpx6">كن شريكًا</Button></Link></div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.7,
            delay: 0.1,
            ease: [0.22, 1, 0.36, 1]
          }} className="relative lg:col-span-6" data-id="element-2827" data-mp="xpxt765c0wlh"><div className="relative aspect-[200/148] w-full lg:-mr-8 lg:w-[calc(100%+6rem)]" data-id="element-2828" data-mp="1qcsaxvarlhv9"><MosqueArchMask src="https://cdn.magicpatterns.com/uploads/hvhTdNSGMR8GbT9gqPkVY1/Partnership.jpg" alt="شريكان يتصافحان أمام شعار المركز الدولي للتعلّم الإسلامي في مكتب حديث" data-id="element-2829" data-mp="1le7aburlow6a" /></div></motion.div>
          </div>
        </Container>
      </section>

      <section className="relative isolate overflow-hidden bg-sage-50/60 py-16 md:py-24" data-id="element-2830" data-mp="1bmtd5tn4g8gb"><PatternDecor side="left" y="top" size="lg" data-id="element-2831" data-mp="295w99tyxgiw7" /><Container data-id="element-2832" data-mp="24duwm6xukq5n"><h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-2833" data-mp="24d308twfeoeb">اختر طريقة دعمك</h2><div className="mt-10 grid gap-5 md:grid-cols-2" data-id="element-2834" data-mp="2bpj4nbjs6xbe">{supportOptions.map((option, index) => <SupportCard key={option.title} option={option} index={index} data-id="element-2835" data-mp="2gnaum00v89cy" data-mpi={index} />)}</div></Container></section>

      <section className="bg-white/60 py-16 md:py-24" data-id="element-2836" data-mp="2lmdznkguifkx"><Container data-id="element-2837" data-mp="2llm4gar5ujv6"><h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-2838" data-mp="2lp07ll7nex3o">نماذج الشراكة</h2><p className="mt-5 max-w-3xl leading-loose text-ink-700" data-id="element-2839" data-mp="2qjnnwwsi0l76">نعمل مع المانحين والمؤسسات والمنظمات التي تشاركنا الالتزام تجاه الأسر المسلمة في أنحاء العالم.</p><div className="mt-10 grid gap-5 md:grid-cols-3" data-id="element-2840" data-mp="2zldp2uc6myi5">{partnershipModels.map((model, index) => <ModelCard key={model.title} model={model} index={index} data-id="element-2841" data-mp="34eg8war5qtl5" data-mpi={index} />)}</div></Container></section>

      <section className="bg-sand-50/60 py-16 md:py-24" data-id="element-2842" data-mp="39crlgt8u7i72"><Container data-id="element-2843" data-mp="39eku2e30gchp"><h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-2844" data-mp="39ecgsl2z755z">كيف نُعدّ التقارير</h2><p className="mt-5 max-w-3xl leading-loose text-ink-700" data-id="element-2845" data-mp="3icwoysawtwfy">نقدم تقارير ووثائق واضحة صُممت للمانحين المؤسسيين والشركاء على المدى الطويل.</p><div className="mt-10 grid gap-5 md:grid-cols-3" data-id="element-2846" data-mp="1j97rsty5gs29">{reports.map((report, index) => <ReportCard key={report.title} report={report} index={index} data-id="element-2847" data-mp="1ea4ll66fofsp" data-mpi={index} />)}</div></Container></section>

      <section className="relative isolate overflow-hidden bg-white/60 py-16 md:py-24" data-id="element-2848" data-mp="1s0zx6q8bhazz"><PatternDecor side="left" y="bottom" size="md" data-id="element-2849" data-mp="1na9rfbvzb64r" /><Container data-id="element-2850" data-mp="1n8gljyt6wpxt"><div className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between" data-id="element-2851" data-mp="1n8qdhrxsp765"><div className="max-w-xl" data-id="element-2852" data-mp="21xmg39h31rzd"><h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-2853" data-mp="2210ken9b4b9g">هل أنت مستعد للدعم؟</h2><p className="mt-5 leading-loose text-ink-700" data-id="element-2854" data-mp="1x2730jqwg040">اختر الخيار الأنسب: التبرع أو المنح الدراسية أو الشراكة المؤسسية. سنرد سريعًا بالخطوات التالية.</p></div><div className="flex flex-col gap-3 md:items-end" data-id="element-2855" data-mp="26z20czbfrugp"><p className="max-w-sm text-sm leading-loose text-ink-500" data-id="element-2856" data-mp="26w6197nuqzqo">تنبيه: نقبل الصدقة والمنح المؤسسية، ولا نقبل الزكاة في هذه المرحلة.</p><div className="flex flex-wrap gap-3" data-id="element-2857" data-mp="ckhptfgtxfb1"><Link to="/ar/partnership/donate-for-children" data-id="element-2858" data-mp="clkd7dey0zm7"><Button variant="primary" data-id="element-2859" data-mp="2gb0qbw7y9dz">تبرّع</Button></Link><Link to="/ar/partnership/become-a-partner" data-id="element-2860" data-mp="2gkr40l6eik7"><Button variant="secondary" data-id="element-2861" data-mp="6hmqchf3eum6">كن شريكًا</Button></Link></div></div></div></Container></section>
    </div>;
}
function SupportCard({
  option,
  index
}: {
  option: SupportOption;
  index: number;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-60px'
  }} transition={{
    duration: 0.5,
    delay: index * 0.06,
    ease: [0.22, 1, 0.36, 1]
  }} whileHover={{
    y: -2
  }} data-id="element-2862" data-mp="3tugqechgouqm"><Link to={option.to} className={`group flex h-full flex-col rounded-2xl border border-ink-900/5 p-6 shadow-soft transition-shadow hover:shadow-card ${option.tone}`} data-id="element-2863" data-mp="3jwsuhj84dh6v"><h3 className="font-display text-xl font-semibold leading-loose text-ink-900" data-id="element-2864" data-mp="35z46i2ir9txl">{option.title}</h3><p className="mt-3 flex-1 text-sm leading-loose text-ink-700" data-id="element-2865" data-mp="3axp9uatb0tq9">{option.body}</p><span className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-forest-700" data-id="element-2866" data-mp="14669rvluyh7d">{option.action}<ArrowLeftIcon size={16} strokeWidth={1.5} className="transition-transform group-hover:-translate-x-0.5" aria-hidden="true" data-id="element-2867" data-mp="192ex3wa7lgpm" /></span></Link></motion.div>;
}
function ModelCard({
  model,
  index
}: {
  model: PartnershipModel;
  index: number;
}) {
  const Icon = model.icon;
  return <motion.article initial={{
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-60px'
  }} transition={{
    duration: 0.5,
    delay: index * 0.06
  }} className={`rounded-2xl border border-ink-900/5 p-6 shadow-soft ${model.tone}`} data-id="element-2868" data-mp="27xsq3blehpnc"><span className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-forest-700/10 bg-white text-forest-700" data-id="element-2869" data-mp="1tzm0eiqluvyr"><Icon size={18} strokeWidth={1.6} aria-hidden="true" data-id="element-2870" data-mp="1p1av8lb999mv" /></span><h3 className="mt-5 font-display text-lg font-semibold leading-loose text-ink-900" data-id="element-2871" data-mp="1kyxn5q91czmx">{model.title}</h3><p className="mt-2 text-sm leading-loose text-ink-700" data-id="element-2872" data-mp="3je0mswe5i6r3">{model.body}</p></motion.article>;
}
function ReportCard({
  report,
  index
}: {
  report: (typeof reports)[number];
  index: number;
}) {
  return <motion.article initial={{
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-60px'
  }} transition={{
    duration: 0.5,
    delay: index * 0.06
  }} className={`rounded-2xl border border-ink-900/5 p-6 shadow-soft ${report.tone}`} data-id="element-2873" data-mp="114xo1q3xlmnq"><CheckCircle2Icon size={20} strokeWidth={1.7} className="text-forest-700" aria-hidden="true" data-id="element-2874" data-mp="1av942b0w3maz" /><h3 className="mt-5 font-display text-lg font-semibold leading-loose text-ink-900" data-id="element-2875" data-mp="1evt1lfpdkrxn">{report.title}</h3><p className="mt-3 text-sm leading-loose text-ink-700" data-id="element-2876" data-mp="i7mpk7q4ix9c">{report.body}</p></motion.article>;
}